import { useState } from "react";
import { Modal, Upload, Button, Alert, Table, Space, Typography, message } from "antd";
import { InboxOutlined, DownloadOutlined } from "@ant-design/icons";
import { downloadBlob } from "../ExportButton";

const { Dragger } = Upload;
const { Text } = Typography;

/**
 * BulkUploadModal — shared CSV/XLSX import dialog (BRD §8.1).
 *
 * Handles the three-step flow every bulk upload in the platform uses:
 *   1. download the template so column names match what the parser expects
 *   2. pick a file (kept client-side until Upload is pressed — no auto-submit)
 *   3. show the per-row error report the backend returns, which is the whole
 *      point of the flow: a partial import must say exactly which rows failed
 *
 * Usage:
 *   <BulkUploadModal
 *     open={open}
 *     title="Upload Payouts"
 *     templateName="EZYGO_Payouts_Template"
 *     onDownloadTemplate={() => downloadPayoutTemplate()}
 *     onUpload={(file) => uploadPayouts(file)}
 *     onClose={() => setOpen(false)}
 *     onSuccess={refresh}
 *   />
 *
 * `extra` renders module-specific controls above the dropzone — e.g. the payout
 * month, which comes from the form rather than the file.
 */
export default function BulkUploadModal({
  open,
  title = "Bulk Upload",
  templateName = "Template",
  accept = ".csv",
  hint = "CSV only, up to 5 MB. Use the template so the column names match.",
  onDownloadTemplate,
  onUpload,
  onClose,
  onSuccess,
  extra,
}) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setUploading(false);
  };

  const handleClose = () => {
    reset();
    if (onClose) onClose();
  };

  const handleTemplate = async () => {
    if (!onDownloadTemplate) return;
    try {
      const raw = await onDownloadTemplate();
      downloadBlob(raw, `${templateName}.csv`);
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || err?.message || "Could not download template");
    }
  };

  const handleUpload = async () => {
    if (!file || !onUpload) return;
    setUploading(true);
    try {
      const response = await onUpload(file);
      // Controllers return { totalRows, importedRows, errorRows, errors }.
      const data = response?.data || response;
      setResult(data);

      if (data?.importedRows > 0) {
        message.success(
          `Imported ${data.importedRows} of ${data.totalRows} row(s)`
        );
        if (onSuccess) onSuccess();
      }
      // A file where every row failed stays open so the errors stay readable.
      if (!data?.errorRows) {
        handleClose();
      }
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const errorColumns = [
    { title: "Row", dataIndex: "row", width: 80 },
    { title: "Error", dataIndex: "message" },
  ];

  return (
    <Modal
      open={open}
      title={title}
      onCancel={handleClose}
      width={640}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {result ? "Close" : "Cancel"}
        </Button>,
        <Button
          key="upload"
          type="primary"
          loading={uploading}
          disabled={!file}
          onClick={handleUpload}
        >
          Upload
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        {onDownloadTemplate && (
          <Button icon={<DownloadOutlined />} onClick={handleTemplate}>
            Download template
          </Button>
        )}

        {extra}

        <Dragger
          accept={accept}
          maxCount={1}
          multiple={false}
          // Keep the file client-side; the Upload button submits it.
          beforeUpload={(f) => {
            setFile(f);
            setResult(null);
            return false;
          }}
          onRemove={() => setFile(null)}
          fileList={file ? [file] : []}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag a file here to upload</p>
          <p className="ant-upload-hint">{hint}</p>
        </Dragger>

        {result && (
          <Alert
            type={result.errorRows ? "warning" : "success"}
            showIcon
            message={
              <Text>
                Imported <b>{result.importedRows ?? 0}</b> of{" "}
                <b>{result.totalRows ?? 0}</b> row(s)
                {result.errorRows ? `, ${result.errorRows} failed` : ""}
              </Text>
            }
          />
        )}

        {result?.errors?.length > 0 && (
          <Table
            size="small"
            rowKey={(r) => `${r.row}-${r.message}`}
            columns={errorColumns}
            dataSource={result.errors}
            pagination={{ pageSize: 5, showSizeChanger: false }}
          />
        )}
      </Space>
    </Modal>
  );
}
